import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "../AdminDashboard.css";
import MyContext from "../../../MyContext";
import NoIndex from "../../../components//NoIndex";
import { Form, Row, Col, Button } from "react-bootstrap";

function AdminDining({ Admin }) {
  const navigate = useNavigate();
  const { baseUrl, config } = useContext(MyContext);

  // Client selector
  const [Clients, setClients] = useState([]);
  const [userID, setUserID] = useState("");
  const [ClientName, setClientName] = useState("");

  // Geo (unchanged)
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLatitude(pos.coords.latitude);
          setLongitude(pos.coords.longitude);
        },
        (err) => console.error("Error getting location:", err)
      );
    }
  }, []);

  // Dates
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Orders + infinite scroll state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);     // for fetches
  const [initialLoading, setInitialLoading] = useState(false); // first page visual
  const [hasMore, setHasMore] = useState(true);

  const pageIndexRef = useRef(1); // start from page 1
  const recPerPageRef = useRef(50); // always 50 as required
  const scrollingRef = useRef(null); // table container ref
  const fetchingRef = useRef(false); // hard lock to avoid double fetch
  const lastUserKeyRef = useRef(""); // to guard against stale appends

  // Dedup helper (by orderid)
  const appendUniqueByOrderId = (current, incoming) => {
    const seen = new Set(current.map((o) => o.orderid));
    const filtered = incoming.filter((o) => !seen.has(o.orderid));
    return current.concat(filtered);
  };

  // Fetch clients (unchanged endpoint)
  const FetchClientCall = async () => {
    try {
      const payload = {
        userid: Admin?.pkid,
        latitude,
        longitude,
      };
      const res = await axios.post(`${baseUrl}/api/Admin/getAllClientList`, payload, config);
      if (res.data.responseCode === "00") {
        setClients(res.data.response || []);
      } else {
        toast.error(res.data.responseMessage || "Failed to fetch client list.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch client list.");
    }
  };

  useEffect(() => {
    FetchClientCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function HandleClientChange({ code, name }) {
    setUserID(code);
    setClientName(name);
  }

  // Reset + first page load whenever user/dates change
  const resetAndLoad = useCallback(async () => {
    if (!userID) return;
    // Create a key that represents the current query context
    const key = `${userID}|${fromDate}|${toDate}`;
    lastUserKeyRef.current = key;

    // Reset
    setOrders([]);
    setHasMore(true);
    pageIndexRef.current = 1;

    // Scroll back to top
    if (scrollingRef.current) {
      scrollingRef.current.scrollTop = 0;
    }

    // Load first page
    setInitialLoading(true);
    await fetchOrdersPage({ isFirst: true, contextKey: key });
    setInitialLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, fromDate, toDate]);

  // Build and hit v3.0 API
  const fetchOrdersPage = async ({ isFirst = false, contextKey }) => {
    if (!userID || fetchingRef.current || !hasMore) return;

    fetchingRef.current = true;
    setLoading(true);

    try {
      const payload = {
        userid: userID,
        status: "hold,completed,open,closed", // string as per new contract
        fromdate: fromDate,
        todate: toDate,
        pageIndex: String(pageIndexRef.current),
        recordsPerPage: String(recPerPageRef.current),
      };

      const res = await axios.post(
        `${baseUrl}/api/Order/v3.0/getPosOrders`,
        payload,
        config
      );

      if (res.data.responseCode === "00") {
        const list = Array.isArray(res.data.response) ? res.data.response : [];

        // Guard against stale appends if user/filters changed mid-request
        if (contextKey && contextKey !== lastUserKeyRef.current) {
          // Discard results from an old context
          return;
        }

        setOrders((prev) => appendUniqueByOrderId(prev, list));

        // If fewer than requested came back, we’re at the end
        if (list.length < recPerPageRef.current) {
          setHasMore(false);
        } else {
          // prepare next page
          pageIndexRef.current = pageIndexRef.current + 1;
        }
      } else {
        // If API returns not OK for a page, stop further loading
        setHasMore(false);
        if (isFirst) {
          setOrders([]);
        }
        toast.error(res.data.responseMessage || "Failed to fetch orders.");
      }
    } catch (e) {
      console.error("Error fetching orders:", e);
      if (isFirst) setOrders([]);
      setHasMore(false);
      toast.error("Error fetching orders.");
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Kick first load when user/dates change
  useEffect(() => {
    resetAndLoad();
  }, [resetAndLoad]);

  // Manual Search button will also re-run reset+first fetch
  const onSearchClick = async () => {
    if (!userID) return toast.info("Please select a client first.");
    await resetAndLoad();
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = scrollingRef.current;
    if (!el || loading || !hasMore) return;

    const threshold = 150; // px from bottom to trigger
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (isNearBottom) {
      // use current context key to prevent stale appends
      const contextKey = lastUserKeyRef.current;
      fetchOrdersPage({ contextKey });
    }
  }, [loading, hasMore]);

  // Attach scroll listener
  useEffect(() => {
    const el = scrollingRef.current;
    if (!el) return;
    const onScroll = () => handleScroll();
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [handleScroll]);

  return (
    <div className="marginRight">
      <>
        <div className="d-flex justify-content-center text-center">
          <ToastContainer />
        </div>

        <div>
          <div
            className="p-4 bg-white"
            style={{ height: "100vh", fontFamily: "Nunito,sans-serif" }}
          >
            <div className="row g-3 px-4 admin-catalogue-wrapper">
              {/* Left Card */}
              <div className="col-md-4 admin-popup">
                <div className="admin-left-card-4 text-white p-4 text-start rounded-2 h-100">
                  <h5 className="fw-bold mb-2 admin-text-animate">POS Orders</h5>
                  <hr className="admin-divider" />
                  <p className="mb-0 small admin-text-animate">
                    Manage and view client orders done via Anlook POS (Point of Sale) system.
                  </p>
                </div>
              </div>

              {/* Right Card */}
              <div className="col-md-8 admin-popup">
                <div className="admin-right-card-4 p-4 rounded-2 h-100">
                  <h5 className="admin-restaurant-subtitle mb-3">Select Restaurant</h5>
                  <select
                    className="form-select form-select-sm admin-custom-dropdown"
                    aria-label="Select Client"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setUserID("");
                        setClientName("");
                        setOrders([]);
                        setHasMore(false);
                        return;
                      }
                      const selectedData = JSON.parse(val);
                      HandleClientChange(selectedData);
                    }}
                  >
                    <option value="">Select Client...</option>
                    {Clients.map((item, index) => (
                      <option
                        key={index}
                        value={JSON.stringify({ code: item.us_code, name: item.us_name })}
                      >
                        {item.us_code} : {item.us_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <hr />

            {userID && (
              <div className="bg-white px-4" style={{ borderRadius: "10px" }}>
                <div className="d-flex justify-content-between align-items-center border-15-only px-2 py-1">
                  <div className="CreateCampaign">POS Orders History of {ClientName}</div>
                </div>

                {/* Filters */}
                <Form className="mb-3">
                  <Row className="align-items-start">
                    <Col md={2}>
                      <Form.Group controlId="fromDate">
                        <Form.Control
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group controlId="toDate">
                        <Form.Control
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1}>
                      <Button
                        onClick={onSearchClick}
                        className="w-100 bg-green-order border border-white"
                        disabled={initialLoading || loading}
                      >
                        {initialLoading || loading ? "Loading..." : "Search"}
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {/* Table with scroll container */}
                <div
                  ref={scrollingRef}
                  className="table-responsive"
                  style={{
                    maxHeight: "68vh",
                    overflowY: "auto",
                    borderRadius: 8,
                    border: "1px solid #e5e5e5",
                  }}
                >
                  <table className="table table-hover table-border border-dark table-bordered align-middle text-center rounded mb-0">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Mobile</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Empty state on first load */}
                      {initialLoading && orders.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-muted py-4">
                            Loading first page...
                          </td>
                        </tr>
                      )}

                      {/* Orders */}
                      {orders.length > 0 &&
                        orders.map((order) => (
                          <tr key={`${order.orderid}`}>
                            <td>{order.daily_order_id || "HOLD"} / {order.orderid}</td>
                            <td>{order.order_date}</td>
                            <td>{order.customer_name || "N/A"}</td>
                            <td>{order.customer_mobile || "-"}</td>
                            <td>
                              {(order.orderType || "").toUpperCase()}
                              {order.orderType === "dining" && ` | Table ${order.table_no}`}
                            </td>
                            <td>₹ {order.totalOrderPrice}</td>
                            <td>
                              <span
                                className={`badge fs-6 ${
                                  order.payment_status === "paid" ? "bg-success" : "bg-danger"
                                }`}
                              >
                                ₹ {order.payment_status === "paid" ? 0 : order.totalOrderPrice}
                              </span>
                            </td>
                          </tr>
                        ))}

                      {/* No data (after loaded) */}
                      {!initialLoading && orders.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-muted py-4">
                            No orders found for selected filters.
                          </td>
                        </tr>
                      )}

                      {/* Loading next page indicator */}
                      {loading && orders.length > 0 && (
                        <tr>
                          <td colSpan="7" className="text-muted py-3">
                            Loading more...
                          </td>
                        </tr>
                      )}

                      {/* End reached */}
                      {!loading && orders.length > 0 && !hasMore && (
                        <tr>
                          <td colSpan="7" className="text-muted py-3">
                            End of results.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    </div>
  );
}

export default AdminDining;
